import React, { useContext, useState } from 'react'

import { useQuery } from '@apollo/react-hooks'
import { compose } from 'recompose'

import CenteredSpinner from 'components/CenteredSpinner'
import Nav from 'components/Nav'
import { ActionType, Address, PageName, User } from 'generated/types'
import { useCardStatusRedirect } from 'pages/CardOrderReview/useCardStatusPolling'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { FunnelContext } from 'utils/FunnelContext'

import { AddressError } from './AddressError'
import { AddressPreview } from './AddressPreview'
import { AddressValidation } from './AddressValidation'
import { EditAddress } from './EditAddress'
import UserQuery from './gql/user.gql'
import { ShippingAddressProvider } from './ShippingAddressContext'
import { ShippingFormattedAddress, ShippingSubRoutes } from './types'
import { formattedAddress, getShippingSubrouteTrackingID } from './utils'

type UserQueryData = {
  residentialAddress?: Address[]
  mailingAddress?: Address[]
  user: User
}

type ShippingAddressProps = {
  residentialAddress: ShippingFormattedAddress
  mailingAddress: ShippingFormattedAddress
  user: User
} & WithFlowRouterProps

const enhance = compose<WithFlowRouterProps, {}>(withFlowRouter)

const ShippingAddress: React.FC<ShippingAddressProps> = ({ route }) => {
  const { loading, data } = useQuery<UserQueryData>(UserQuery, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  })

  const { isDynamicFunnelEnabled, routeToPage } = useContext(FunnelContext)
  const [subRoute, setSubRoute] = useState(ShippingSubRoutes.Preview)

  const handleNext = () => {
    // TODO: Tracking
    isDynamicFunnelEnabled
      ? routeToPage(PageName.ShippingAddress, ActionType.Continue)
      : route({ from: FlowRoute.ShippingAddress, action: 'continue' })
  }

  const loadingCardStatus = useCardStatusRedirect(() => {
    isDynamicFunnelEnabled
      ? routeToPage(PageName.ShippingAddress, ActionType.Skip)
      : route({ from: FlowRoute.ShippingAddress, action: 'skip' })
  })

  if (loading || loadingCardStatus) {
    return <CenteredSpinner />
  }

  return (
    <>
      <Nav hideProgressBar back backClickTracker={getShippingSubrouteTrackingID(subRoute)} />
      <ShippingAddressProvider
        setSubRoute={setSubRoute}
        defaultAddresses={{
          residential: formattedAddress(data.residentialAddress[0]),
          mailing: formattedAddress(data.mailingAddress[0]),
        }}
      >
        {
          {
            [ShippingSubRoutes.Preview]: (
              <AddressPreview user={data.user} handleNext={handleNext} />
            ),
            [ShippingSubRoutes.Edit]: <EditAddress />,
            [ShippingSubRoutes.Error]: <AddressError />,
            [ShippingSubRoutes.Validation]: <AddressValidation />,
          }[subRoute]
        }
      </ShippingAddressProvider>
    </>
  )
}

export default enhance(ShippingAddress)
